import * as THREE from 'three';

// Cyan Ice skin color palette - cold, crystalline feel
export const ICE_COLORS = {
  primary: '#40E0D0',        // Turquoise ice
  secondary: '#E0FFFF',      // Light cyan (ice highlights)
  emissive: '#00CED1',       // Dark turquoise glow
  background: '#0a1520',     // Deep frozen blue-black
  accent: '#87CEEB',         // Sky blue for effects
  gridLine: '#1a3040',       // Darker ice blue grid
  border: '#00FFFF',         // Bright cyan border
  borderGlow: '#40E0D0',     // Turquoise border glow
  frost: '#B0E0E6',          // Powder blue frost
  icicle: '#E0FFFF',         // Light cyan icicles
  snow: '#FFFFFF',           // White snowflakes
  crystal: '#87CEEB',        // Sky blue crystals
};

// Unified cyan crystal palette - all blocks share the same elegant cyan tones
// This creates a cohesive, premium "Prime" look
const CYAN_CRYSTAL_PRIMARY = '#00E5E5';    // Bright cyan crystal
const CYAN_CRYSTAL_EMISSIVE = '#00CED1';   // Deep turquoise glow

export const ICE_TETROMINO_COLORS = {
  'l-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  'j-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  't-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  'square-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  'line-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  's-tetromino-active': CYAN_CRYSTAL_PRIMARY,
  'z-tetromino-active': CYAN_CRYSTAL_PRIMARY,
};

// Unified emissive glow for all blocks
export const ICE_TETROMINO_EMISSIVE = {
  'l-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  'j-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  't-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  'square-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  'line-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  's-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
  'z-tetromino-active': CYAN_CRYSTAL_EMISSIVE,
};

// Get block color based on tetromino type
export const getBlockColor = (colorClass) => {
  return ICE_TETROMINO_COLORS[colorClass] || ICE_COLORS.primary;
};

// Get emissive color for glow
export const getEmissiveColor = (colorClass) => {
  return ICE_TETROMINO_EMISSIVE[colorClass] || ICE_COLORS.emissive;
};

// Create ice-styled material for blocks - glossy, crystalline
export const createIceMaterial = (options = {}) => {
  const {
    color = ICE_COLORS.primary,
    emissive = ICE_COLORS.emissive,
    emissiveIntensity = 0.6,
    metalness = 0.7,        // Higher for ice reflection
    roughness = 0.1,        // Low for glossy ice
    opacity = 1.0,
    transparent = false,
  } = options;

  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(color),
    emissive: new THREE.Color(emissive),
    emissiveIntensity,
    metalness,
    roughness,
    opacity,
    transparent,
  });
};

// Create frosted glass material for border
export const createFrostedGlassMaterial = () => {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(ICE_COLORS.border),
    emissive: new THREE.Color(ICE_COLORS.borderGlow),
    emissiveIntensity: 0.4,
    metalness: 0.8,
    roughness: 0.2,
    opacity: 0.9,
    transparent: true,
  });
};

// Create ghost (hard drop preview) material with ice effect
export const createGhostMaterial = (baseColor) => {
  return new THREE.MeshStandardMaterial({
    color: new THREE.Color(baseColor),
    emissive: new THREE.Color(baseColor),
    emissiveIntensity: 0.4,
    metalness: 0.5,
    roughness: 0.3,
    opacity: 0.35,
    transparent: true,
  });
};

// Create edge highlight material
export const createEdgeMaterial = () => {
  return new THREE.LineBasicMaterial({
    color: new THREE.Color(ICE_COLORS.frost),
    linewidth: 2,
    opacity: 0.7,
    transparent: true,
  });
};

// Theme configuration
export const THEME_CONFIG = {
  name: 'Cyan',
  background: '#0a1520',      // Deep frozen blue-black
  bloomIntensity: 0.7,        // Slightly higher for ice glow
  bloomThreshold: 0.12,
  vignetteOffset: 0.25,
  vignetteDarkness: 0.5,
};
